import { TabPane, Tabs } from 'fave-ui';
import { DealsTab } from './DealsTab';
import { ECardsTab } from './ECardsTab';
import { FavepayTab } from './FavepayTab';
import { PaymentModeTab } from './PaymentModeTab/PaymentModeTab';
import { InstalmentPaymentPlanTab } from './InstalmentPaymentPlanTab';
import { ContractMigrationTab } from './ContractMigrationTab';
import {
  PageHeaderWithSpace,
  SearchBarQueryString,
} from '../../components/common';
import { useNavigate } from 'react-router';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import {
  CLEVERTAP_ADDITIONAL,
  CLEVERTAP_SCREEN_NAME,
  CLEVERTAP_TAPPED_ON,
} from '../../helpers/ClevertapKeys';
import useOnceCallback from '../../hooks/useOnceCallback';
import useCleverTap from '../../hooks/useCleverTap';
import { useUserContext } from '../../contexts/UserContext';
import LockPage from '../../components/LockPage';
import styles from '../MySubmissionView/style.module.css';
import { mySubmissionElementIDs } from './elementId';
import { FavePassTab } from './FavePassTab';
import { useFetchFlipper } from '../../services/merchants';
import { StampCardTab } from './StampCardTab';

const productTabKeys = {
  favepay: 'favepay',
  deals: 'deals',
  ecards: 'ecards',
  bnpl: 'bnpl',
  payment_mode: 'payment_mode',
  ipp: 'ipp',
  contract_migration: 'contract_migration',
  fave_pass: 'fave_pass',
  stamp_card: 'stamp_card',
};

const MySubmissionView = () => {
  const { data: isFavePassOnboardingDisabled } = useFetchFlipper({
    key: 'disable_fave_pass_onboarding',
  });
  const { data: isStampCardOnboardingDisabled } = useFetchFlipper({
    key: 'hide_stamp_cards_onboarding',
  });
  const cleverTap = useCleverTap();
  const navigate = useNavigate();
  const { getPageSettings } = useUserContext();
  const pageSettings = getPageSettings('merchant_connect');
  const enableMerchantConnect = pageSettings?.show;
  const [queryString] = useSearchParams();
  const { tab } = Object.fromEntries(queryString);
  const activeTab =
    productTabKeys[tab as keyof typeof productTabKeys] ||
    productTabKeys.favepay;

  useOnceCallback(() =>
    cleverTap.screenDisplayed.push({
      screen_name: CLEVERTAP_SCREEN_NAME.MY_SUBMISSION,
      platform: CLEVERTAP_ADDITIONAL.MERCHANT_CONNECT,
    }),
  );

  const onTapClickHandler = (key: string) => {
    cleverTap.tapped.push({
      screen_name: CLEVERTAP_SCREEN_NAME.PRODUCT_DASHBOARD,
      tapped_on:
        CLEVERTAP_TAPPED_ON.BUTTON_PRODUCT_TAP[
          key as keyof typeof CLEVERTAP_TAPPED_ON.BUTTON_PRODUCT_TAP
        ],
      platform: CLEVERTAP_ADDITIONAL.MERCHANT_CONNECT,
    });

    const params = { tab: key };

    navigate(
      `/admin/merchant_connect/my_submissions?${createSearchParams(params)}`,
    );
  };

  const productTabs = [
    {
      tab: 'FavePay',
      dataId: mySubmissionElementIDs.favePayTab,
      key: productTabKeys.favepay,
      children: <FavepayTab />,
      show: true,
    },
    {
      tab: 'Deals',
      dataId: mySubmissionElementIDs.dealsTab,
      key: productTabKeys.deals,
      children: <DealsTab />,
      show: true,
    },
    {
      tab: 'eCards',
      dataId: mySubmissionElementIDs.eCardsTab,
      key: productTabKeys.ecards,
      children: <ECardsTab />,
      show: true,
    },
    {
      tab: 'Payment Mode',
      dataId: mySubmissionElementIDs.paymentModeTab,
      key: productTabKeys.payment_mode,
      children: <PaymentModeTab />,
      show: true,
    },
    {
      tab: 'Instalment Payment Plan',
      dataId: mySubmissionElementIDs.ippTab,
      key: productTabKeys.ipp,
      children: <InstalmentPaymentPlanTab />,
      show: true,
    },
    {
      tab: 'Contract Migration',
      dataId: mySubmissionElementIDs.contractMigrationTab,
      key: productTabKeys.contract_migration,
      children: <ContractMigrationTab />,
      show: true,
    },
    {
      tab: 'Fave Passes',
      dataId: mySubmissionElementIDs.favePassTab,
      key: productTabKeys.fave_pass,
      children: <FavePassTab />,
      show: !isFavePassOnboardingDisabled?.value,
    },
    {
      tab: 'Stamp Cards',
      dataId: mySubmissionElementIDs.stampCardTab,
      key: productTabKeys.stamp_card,
      children: <StampCardTab />,
      show: !isStampCardOnboardingDisabled?.value,
    },
  ];

  return enableMerchantConnect ? (
    <div>
      <PageHeaderWithSpace
        title={'My Submissions'}
        subTitle={'All your submission progress can be tracked here'}
      />
      <Tabs
        tabBarExtraContent={<SearchBarQueryString />}
        defaultActiveKey={productTabKeys.favepay}
        activeKey={activeTab}
        onTabClick={key => onTapClickHandler(key)}
        className={styles.tabs}
      >
        {productTabs.map(
          ({ tab, key, dataId, show, ...restItems }) =>
            show && (
              <TabPane
                {...restItems}
                tab={<span data-id={dataId}>{tab}</span>}
                key={key}
              />
            ),
        )}
      </Tabs>
    </div>
  ) : (
    <LockPage />
  );
};

export default MySubmissionView;
